import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { elearningAPI } from '../../../services/elearningApi';
import {
  Course,
  CreateCourseInput,
  UpdateCourseInput,
  CourseDifficulty,
  CourseStatus,
} from '../../../types/elearning';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton';
import ErrorState from '../../../components/ui/ErrorState';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { useToast } from '../../../hooks/useToast';
import ToastContainer from '../../../components/ui/ToastContainer';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { moveItem, ReorderHandle } from '../../../components/elearning/admin/reorder.tsx';
import { Audience, ClinicalArea, Topic } from '../../../types/metadata';
import { audienceConfig, clinicalAreaLabels, formatTopicLabel } from '../../../utils/metadataHelpers';

const COURSE_ACCESS_OPTIONS = [
  { value: 'PUBLIC', label: 'Public' },
  { value: 'ONTARIO_HEALTHCARE_PROFESSIONALS', label: 'Ontario Healthcare Professional' },
  { value: 'SPECIFIC_USERS', label: 'Special access' },
  { value: 'ADMIN', label: 'Administrator' },
];

function facetOptions(entries: Array<[string, string]>) {
  return entries
    .filter(([value]) => value !== 'Unknown')
    .sort((a, b) => a[1].localeCompare(b[1]));
}

const AUDIENCE_OPTIONS = facetOptions(
  Object.values(Audience).map((value) => [value, audienceConfig[value].label] as [string, string])
);
const TOPIC_OPTIONS = facetOptions(
  Object.values(Topic).map((value) => [value, formatTopicLabel(value)] as [string, string])
);
const CLINICAL_AREA_OPTIONS = facetOptions(
  Object.values(ClinicalArea).map((value) => [value, clinicalAreaLabels[value]] as [string, string])
);

export default function CourseBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  // Form state - define first
  const [title, setTitle] = useState('');
  usePageTitle(isEditing ? (title || 'Edit Course') : 'New Course');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [objectives, setObjectives] = useState<string[]>(['']);
  const [prerequisites, setPrerequisites] = useState<string[]>([]);
  const [prerequisiteChoice, setPrerequisiteChoice] = useState('');
  const [catalog, setCatalog] = useState<Course[]>([]);
  const [estimatedTime, setEstimatedTime] = useState(60);
  const [difficulty, setDifficulty] = useState<CourseDifficulty>(CourseDifficulty.BEGINNER);
  const [status, setStatus] = useState<CourseStatus>(CourseStatus.DRAFT);
  const [audiences, setAudiences] = useState<string[]>([]);
  const [topics, setTopics] = useState<string[]>([]);
  const [clinicalAreas, setClinicalAreas] = useState<string[]>([]);
  const [accessLevel, setAccessLevel] = useState('PUBLIC');

  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(isEditing); // Only loading when editing (fetching course)
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingModuleId, setDeletingModuleId] = useState<string | null>(null);
  const draggingModuleId = useRef<string | null>(null);
  const [dropModuleId, setDropModuleId] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedData, setLastSavedData] = useState<string>('');
  const { toasts, success, error: showError, removeToast } = useToast();
  
  // Track initial data for comparison (only when editing and course is loaded)
  // This should only run once when course data is first loaded
  useEffect(() => {
    if (isEditing && course && course.id && !lastSavedData) {
      const initialData = JSON.stringify({
        title: course.title,
        slug: course.slug,
        description: course.description || '',
        objectives: course.objectives,
        prerequisites: course.prerequisites,
        audiences: course.audiences || [],
        topics: course.topics || [],
        clinicalAreas: course.clinicalAreas || [],
        accessLevel: course.accessLevel || 'PUBLIC',
        estimatedTime: course.estimatedTime,
        difficulty: course.difficulty,
        status: course.status,
      });
      setLastSavedData(initialData);
    }
  }, [isEditing, course?.id]); // Only run when course ID changes

  // Check for unsaved changes (only when editing)
  useEffect(() => {
    if (isEditing && lastSavedData) {
      const currentData = JSON.stringify({
        title,
        slug,
        description,
        objectives,
        prerequisites,
        audiences,
        topics,
        clinicalAreas,
        accessLevel,
        estimatedTime,
        difficulty,
        status,
      });
      setHasUnsavedChanges(currentData !== lastSavedData);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [title, slug, description, objectives, prerequisites, audiences, topics, clinicalAreas, accessLevel, estimatedTime, difficulty, status, lastSavedData, isEditing]);

  useEffect(() => {
    elearningAPI.fetchCourses().then(setCatalog).catch(() => setCatalog([]));
  }, []);

  useEffect(() => {
    if (isEditing && id) {
      const fetchCourse = async () => {
        try {
          setLoading(true);
          const courseData = await elearningAPI.fetchCourseById(id);
          if (!courseData) {
            setError(new Error('Course not found'));
            return;
          }
          setCourse(courseData);
          setTitle(courseData.title);
          setSlug(courseData.slug);
          setDescription(courseData.description || '');
          setObjectives(courseData.objectives.length > 0 ? courseData.objectives : ['']);
          setPrerequisites(courseData.prerequisites || []);
          setAudiences(courseData.audiences || []);
          setTopics(courseData.topics || []);
          setClinicalAreas(courseData.clinicalAreas || []);
          setAccessLevel(courseData.accessLevel || 'PUBLIC');
          setEstimatedTime(courseData.estimatedTime);
          setDifficulty(courseData.difficulty);
          setStatus(courseData.status);
        } catch (err) {
          setError(err instanceof Error ? err : new Error('Failed to load course'));
        } finally {
          setLoading(false);
        }
      };
      fetchCourse();
    } else {
      // For new courses, ensure loading is false
      setLoading(false);
    }
  }, [id, isEditing]);

  const { handleNavigation } = useUnsavedChanges({
    hasUnsavedChanges,
    message: 'You have unsaved changes. Are you sure you want to leave?',
  });

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
  };

  const handleTitleBlur = () => {
    // Only auto-generate slug on blur if slug is empty and we have a title
    if (!slug && title.trim()) {
      setSlug(generateSlug(title));
    }
  };

  const handleAddObjective = () => {
    setObjectives([...objectives, '']);
  };

  const handleObjectiveChange = (index: number, value: string) => {
    const newObjectives = [...objectives];
    newObjectives[index] = value;
    setObjectives(newObjectives);
  };

  const handleObjectiveKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentValue = objectives[index]?.trim();
      if (currentValue) {
        // Save current objective and add new one
        const newObjectives = [...objectives];
        newObjectives[index] = currentValue;
        newObjectives.push('');
        setObjectives(newObjectives);
        // Focus will be handled by the new input field being rendered
        setTimeout(() => {
          const nextInput = document.querySelector(`input[placeholder="Objective ${newObjectives.length}"]`) as HTMLInputElement;
          if (nextInput) {
            nextInput.focus();
          }
        }, 0);
      }
    }
  };

  const handleRemoveObjective = (index: number) => {
    setObjectives(objectives.filter((_, i) => i !== index));
  };

  const handleSave = async (publish: boolean = false) => {
    if (!title.trim()) {
      showError('Please enter a course title');
      return;
    }

    try {
      setSaving(true);

      const savedStatus = publish ? CourseStatus.PUBLISHED : status;
      const courseData: CreateCourseInput | UpdateCourseInput = {
        title: title.trim(),
        slug: slug.trim() || generateSlug(title),
        description: description.trim() || undefined,
        objectives: objectives.filter((obj) => obj.trim().length > 0),
        prerequisites: prerequisites,
        audiences,
        topics,
        clinicalAreas,
        accessLevel,
        estimatedTime,
        difficulty,
        ...(isEditing && { status: savedStatus }),
      };

      if (isEditing && id) {
        await elearningAPI.updateCourse(id, courseData);
        setStatus(savedStatus);
        // Update last saved data
        setLastSavedData(JSON.stringify({
          title: title.trim(),
          slug: slug.trim() || generateSlug(title),
          description: description.trim() || '',
          objectives: objectives.filter((obj) => obj.trim().length > 0),
          prerequisites,
          audiences,
          topics,
          clinicalAreas,
          accessLevel,
          estimatedTime,
          difficulty,
          status: savedStatus,
        }));
        setHasUnsavedChanges(false);
      } else {
        const newCourse = await elearningAPI.createCourse(courseData as CreateCourseInput);
        navigate(`/elearning/admin/courses/${newCourse.id}/edit`);
        return;
      }

      // Show success message
      const savedLabel =
        savedStatus === CourseStatus.PUBLISHED
          ? 'Course published successfully!'
          : savedStatus === CourseStatus.ARCHIVED
            ? 'Course archived'
            : 'Course saved as draft';
      success(savedLabel);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCourse = async () => {
    if (!id) return;
    if (!confirm(`Delete "${title.trim() || 'this course'}"? This cannot be undone.`)) return;
    try {
      setDeleting(true);
      await elearningAPI.deleteCourse(id);
      navigate('/elearning');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete course');
      setDeleting(false);
    }
  };

  const handleDropModule = async (targetId: string) => {
    const fromId = draggingModuleId.current;
    setDropModuleId(null);
    if (!fromId || fromId === targetId || !course?.modules) return;
    const previous = [...course.modules].sort((a, b) => a.order - b.order);
    const next = moveItem(previous, fromId, targetId);
    if (next === previous) return;
    const ordered = next.map((module, index) => ({ ...module, order: index + 1 }));
    setCourse({ ...course, modules: ordered });
    try {
      await elearningAPI.reorderModules(
        course.id,
        ordered.map((module) => ({ id: module.id, order: module.order }))
      );
    } catch (err) {
      setCourse({ ...course, modules: previous });
      showError(err instanceof Error ? err.message : 'Failed to reorder modules');
    }
  };

  const handleDeleteModule = async (moduleId: string, moduleTitle: string) => {
    if (!confirm(`Delete module "${moduleTitle}"? This cannot be undone.`)) return;
    try {
      setDeletingModuleId(moduleId);
      await elearningAPI.deleteModule(moduleId);
      setCourse((current) =>
        current
          ? { ...current, modules: (current.modules || []).filter((item) => item.id !== moduleId) }
          : current
      );
      success('Module deleted');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete module');
    } finally {
      setDeletingModuleId(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={10} />
      </div>
    );
  }

  if (error && isEditing) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState error={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  // For new courses, always render the form
  // Debug: Ensure component renders
  if (!isEditing && loading) {
    // This shouldn't happen, but ensure loading is false for new courses
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={10} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: 'eLearning', to: '/elearning' },
          { label: isEditing ? 'Edit Course' : 'Create Course' },
        ]}
      />

      <Link
        to="/elearning/admin/courses"
        className="inline-flex items-center gap-2 text-primary hover:text-primary-dark mb-6 transition-colors"
      >
        <ArrowLeftIcon className="h-5 w-5" />
        <span>Back to Courses</span>
      </Link>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {isEditing ? 'Edit Course' : 'Create New Course'}
        </h1>
        <p className="text-gray-600">
          {isEditing
            ? 'Update course information and settings'
            : 'Create a new course for the eLearning platform'}
        </p>
      </div>

      <Card>
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course Title *
                </label>
                <Input
                  type="text"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  onBlur={handleTitleBlur}
                  placeholder="e.g., Introduction to Transfusion Medicine"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Slug
                </label>
                <Input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="auto-generated-from-title"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used in the course URL. Leave empty to auto-generate from title.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:border-primary focus:ring-2 focus:ring-primary focus:ring-offset-0"
                  placeholder="Brief description of the course..."
                />
              </div>
            </div>
          </div>

          {/* Learning Objectives */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Learning Objectives</h2>
            <div className="space-y-3">
              {objectives.map((objective, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Input
                    type="text"
                    value={objective}
                    onChange={(e) => handleObjectiveChange(index, e.target.value)}
                    onKeyDown={(e) => handleObjectiveKeyDown(index, e)}
                    placeholder={`Objective ${index + 1}`}
                    className="flex-1"
                  />
                  {objectives.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveObjective(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      aria-label="Remove objective"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              ))}
              <Button
                variant="tertiary"
                onClick={handleAddObjective}
                className="w-full sm:w-auto"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Objective
              </Button>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Prerequisites</h2>
            <div className="space-y-3">
              {prerequisites.map((courseId) => {
                const required = catalog.find((item) => item.id === courseId);
                return (
                  <div key={courseId} className="flex items-center gap-2">
                    <p className="flex-1 text-gray-900">{required?.title || 'Unknown course'}</p>
                    <button
                      type="button"
                      onClick={() => setPrerequisites(prerequisites.filter((id) => id !== courseId))}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      aria-label={`Remove ${required?.title || 'prerequisite'}`}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                );
              })}
              <Select
                value={prerequisiteChoice}
                onChange={(e) => {
                  const courseId = e.target.value;
                  if (!courseId || prerequisites.includes(courseId)) return;
                  setPrerequisites([...prerequisites, courseId]);
                  setPrerequisiteChoice('');
                }}
                options={[
                  { value: '', label: 'Add a prerequisite course' },
                  ...catalog
                    .filter((item) => item.id !== id && !prerequisites.includes(item.id))
                    .sort((a, b) => a.title.localeCompare(b.title))
                    .map((item) => ({ value: item.id, label: item.title })),
                ]}
              />
            </div>
          </div>

          <FacetEditor
            label="Audience"
            placeholder="Add an audience"
            values={audiences}
            options={AUDIENCE_OPTIONS}
            onChange={setAudiences}
          />
          <FacetEditor
            label="Topics"
            placeholder="Add a topic"
            values={topics}
            options={TOPIC_OPTIONS}
            onChange={setTopics}
          />
          <FacetEditor
            label="Clinical areas"
            placeholder="Add a clinical area"
            values={clinicalAreas}
            options={CLINICAL_AREA_OPTIONS}
            onChange={setClinicalAreas}
          />

          {/* Course Settings */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Course Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated Time (minutes)
                </label>
                <Input
                  type="number"
                  value={estimatedTime}
                  onChange={(e) => setEstimatedTime(parseInt(e.target.value) || 0)}
                  min={1}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Difficulty Level
                </label>
                <Select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value as CourseDifficulty)}
                  options={[
                    { value: CourseDifficulty.BEGINNER, label: 'Beginner' },
                    { value: CourseDifficulty.INTERMEDIATE, label: 'Intermediate' },
                    { value: CourseDifficulty.ADVANCED, label: 'Advanced' },
                  ]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access level
                </label>
                <Select
                  value={accessLevel}
                  onChange={(e) => setAccessLevel(e.target.value)}
                  options={COURSE_ACCESS_OPTIONS}
                />
              </div>

              {isEditing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as CourseStatus)}
                    options={[
                      { value: CourseStatus.DRAFT, label: 'Draft' },
                      { value: CourseStatus.PUBLISHED, label: 'Published' },
                      { value: CourseStatus.ARCHIVED, label: 'Archived' },
                    ]}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <div className="flex items-center gap-4 text-sm">
              {hasUnsavedChanges && (
                <span className="text-yellow-600 font-medium flex items-center gap-2">
                  <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
                  You have unsaved changes
                </span>
              )}
              {!hasUnsavedChanges && isEditing && (
                <span className="text-green-600 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  All changes saved
                </span>
              )}
              {!hasUnsavedChanges && !isEditing && (
                <span className="text-gray-600">Course will be created as a draft</span>
              )}
              {isEditing && (
                <Button
                  variant="ghost"
                  className="!px-3 !py-2 !text-error hover:!bg-red-50"
                  onClick={handleDeleteCourse}
                  disabled={saving || deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete course'}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                onClick={() => handleNavigation('/elearning')}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => handleSave(false)}
                disabled={saving || !title.trim()}
                className={hasUnsavedChanges ? 'ring-2 ring-yellow-500' : ''}
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Saving...
                  </span>
                ) : (
                  isEditing ? 'Save Changes' : 'Save as Draft'
                )}
              </Button>
              {isEditing && (
                <Button
                  variant="primary"
                  onClick={() => handleSave(true)}
                  disabled={saving || !title.trim() || status === CourseStatus.PUBLISHED}
                >
                  {saving ? 'Publishing...' : 'Publish Course'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Module Management Section */}
      {isEditing && course && (
        <div className="mt-8">
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Modules</h2>
                <Link
                  to={`/elearning/admin/courses/${course.id}/modules/new`}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors font-medium text-sm"
                >
                  <PlusIcon className="h-5 w-5 inline mr-2" />
                  Add Module
                </Link>
              </div>
              {course.modules && course.modules.length > 0 ? (
                <div className="space-y-2">
                  {[...course.modules]
                    .sort((a, b) => a.order - b.order)
                    .map((module, index) => (
                    <div
                      key={module.id}
                      onDragOver={(event) => {
                        if ((course.modules?.length || 0) < 2) return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'move';
                        if (dropModuleId !== module.id) setDropModuleId(module.id);
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        void handleDropModule(module.id);
                      }}
                      onDragLeave={(event) => {
                        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
                        if (dropModuleId === module.id) setDropModuleId(null);
                      }}
                      className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors ${
                        dropModuleId === module.id && draggingModuleId.current !== module.id
                          ? 'ring-2 ring-primary'
                          : ''
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        {(course.modules?.length || 0) > 1 && (
                          <ReorderHandle
                            label={`Drag to reorder ${module.title}`}
                            onDragStart={(event) => {
                              draggingModuleId.current = module.id;
                              event.dataTransfer.effectAllowed = 'move';
                              event.dataTransfer.setData('text/plain', module.id);
                            }}
                            onDragEnd={() => {
                              draggingModuleId.current = null;
                              setDropModuleId(null);
                            }}
                          />
                        )}
                        <span className="text-sm font-medium text-gray-500 w-8">
                          {index + 1}
                        </span>
                        <div>
                          <h3 className="font-semibold text-gray-900">{module.title}</h3>
                          <p className="text-sm text-gray-500">
                            {module.estimatedTime} min • {module.content.slides.length} slides
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/elearning/admin/courses/${course.id}/modules/${module.id}/edit`}
                          className="text-primary hover:text-primary-dark font-medium text-sm px-2 py-2"
                        >
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="p-2 text-error hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                          aria-label={`Delete ${module.title}`}
                          disabled={deletingModuleId === module.id}
                          onClick={() => handleDeleteModule(module.id, module.title)}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No modules yet. Add your first module to get started.</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

function FacetEditor({
  label,
  placeholder,
  values,
  options,
  onChange,
}: {
  label: string;
  placeholder: string;
  values: string[];
  options: Array<[string, string]>;
  onChange: (values: string[]) => void;
}) {
  const [choice, setChoice] = useState('');
  const labels = new Map(options);

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">{label}</h2>
      <div className="space-y-3">
        {values.map((value) => (
          <div key={value} className="flex items-center gap-2">
            <p className="flex-1 text-gray-900">{labels.get(value) || value}</p>
            <button
              type="button"
              onClick={() => onChange(values.filter((item) => item !== value))}
              className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
              aria-label={`Remove ${labels.get(value) || value}`}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        ))}
        <Select
          value={choice}
          onChange={(e) => {
            const next = e.target.value;
            if (!next || values.includes(next)) return;
            onChange([...values, next]);
            setChoice('');
          }}
          options={[
            { value: '', label: placeholder },
            ...options
              .filter(([value]) => !values.includes(value))
              .map(([value, optionLabel]) => ({ value, label: optionLabel })),
          ]}
        />
      </div>
    </div>
  );
}

